import { 
  Users, Calendar, TrendingUp, Award, Clock, Target,
  Brain, Zap, BarChart3, Shield, Lock, Gauge,
  Database, CheckCircle, Globe, Star, Settings,
  UserCheck, Lightbulb, Briefcase, Heart, GraduationCap,
  PieChart, MessageSquare, Building, DollarSign, FileText,
  Search, Headphones, Activity, Wifi
} from 'lucide-react';
import { ProductFeatureConfig } from './types';

export const hrSolutionsConfigs: Record<string, ProductFeatureConfig> = {
  'exit-interview-management': {
    keyBenefits: [
      {
        icon: Users,
        title: 'Retention Rate Improvement',
        description: 'Reduce employee turnover by 40% through actionable insights from comprehensive exit interview analysis.',
        metrics: '40% retention boost',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Turnover Analytics',
        description: 'AI-powered analytics that predict employee turnover risk 6 months in advance with 90% accuracy.',
        metrics: '90% accuracy',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Award,
        title: 'Culture Enhancement Insights',
        description: 'Transform exit feedback into actionable culture improvement strategies that boost employee satisfaction.',
        metrics: 'Culture insights',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Departmental Analysis',
        description: 'Identify problematic departments and managers with detailed analytics and trending reports.',
        metrics: 'Department insights',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Target,
        title: 'Cost Savings Tracking',
        description: 'Track and measure the financial impact of retention improvements, saving up to $75K per prevented exit.',
        metrics: '$75K savings/exit',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Streamlined Process',
        description: 'Reduce exit interview administration time by 80% with automated scheduling and data collection.',
        metrics: '80% time saved',
        color: 'from-yellow-500 to-orange-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI Sentiment Analysis',
        description: 'Advanced NLP algorithms analyze interview responses for sentiment, themes, and emotional indicators.',
        metrics: 'AI sentiment analysis',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Alert System',
        description: 'Instant notifications when exit patterns indicate systemic issues or high-risk scenarios.',
        metrics: 'Real-time alerts',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Modeling',
        description: 'Machine learning models that identify employees at risk of leaving before they make the decision.',
        metrics: 'Predictive insights',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Benchmark Comparisons',
        description: 'Industry benchmarking that compares your retention metrics against similar organizations.',
        metrics: 'Industry benchmarks',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Custom Intervention Workflows',
        description: 'Automated workflows that trigger specific retention actions based on exit interview findings.',
        metrics: 'Automated interventions',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Users,
        title: 'Anonymous Feedback Integration',
        description: 'Secure anonymous feedback collection that encourages honest responses and protects employee privacy.',
        metrics: 'Anonymous feedback',
        color: 'from-green-500 to-teal-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },
  'recruitment-management-system': {
    keyBenefits: [
      {
        icon: Users,
        title: 'Hiring Speed Acceleration',
        description: 'Reduce time-to-hire by 60% with AI-powered candidate screening and automated workflow management.',
        metrics: '60% faster hiring',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Target,
        title: 'Quality Candidate Matching',
        description: 'AI algorithms improve candidate-role matching accuracy by 85%, resulting in better long-term hires.',
        metrics: '85% match accuracy',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: TrendingUp,
        title: 'Cost Per Hire Reduction',
        description: 'Lower recruitment costs by 45% through process optimization and reduced external agency dependence.',
        metrics: '45% cost reduction',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: BarChart3,
        title: 'Recruitment Analytics',
        description: 'Comprehensive analytics providing insights into hiring funnel performance and recruiter productivity.',
        metrics: 'Full analytics',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Automated Scheduling',
        description: 'Smart interview scheduling that coordinates multiple stakeholders and reduces back-and-forth by 90%.',
        metrics: '90% less coordination',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Award,
        title: 'Candidate Experience Excellence',
        description: 'Deliver exceptional candidate experiences that improve employer brand and increase acceptance rates.',
        metrics: 'Premium experience',
        color: 'from-indigo-500 to-blue-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI Resume Screening',
        description: 'Advanced machine learning models automatically screen and rank candidates based on job requirements.',
        metrics: 'AI screening',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Predictive Hiring Success',
        description: 'Predict candidate success probability using historical data and performance correlation analysis.',
        metrics: 'Success prediction',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Talent Pipeline Management',
        description: 'Build and nurture talent pipelines for future hiring needs with automated candidate engagement.',
        metrics: 'Pipeline automation',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Bias Reduction Technology',
        description: 'AI-powered bias detection and mitigation ensuring fair and diverse hiring practices.',
        metrics: 'Bias reduction',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Market Intelligence',
        description: 'Real-time salary benchmarking and market analysis for competitive offer recommendations.',
        metrics: 'Market intelligence',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Multi-Channel Sourcing',
        description: 'Integrated sourcing across job boards, social media, and professional networks with unified management.',
        metrics: 'Multi-channel sourcing',
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  // Merged HR Products
  'complete-hr-management-platform': {
    keyBenefits: [
      {
        icon: Users,
        title: 'Complete Employee Lifecycle Management',
        description: 'Unified employee records from hire to retire, reducing administrative overhead by 70% with seamless SMB-focused automation.',
        metrics: '70% admin reduction',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Automated Payroll & Benefits',
        description: 'Streamlined payroll processing with tax calculations, compliance checks, and comprehensive benefits administration.',
        metrics: 'Automated payroll',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Award,
        title: 'HR Analytics & Workforce Insights',
        description: 'Real-time workforce analytics and strategic HR insights improving satisfaction by 60% with predictive AI.',
        metrics: '60% satisfaction boost',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Time & Attendance Automation',
        description: 'Automated time tracking with overtime calculations, leave management, and compliance monitoring.',
        metrics: 'Automated tracking',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Compliance & Audit Readiness',
        description: 'Automated compliance monitoring for labor laws, EEOC requirements, and complete audit trail management.',
        metrics: 'Compliance automation',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Target,
        title: 'SMB Growth Scalability',
        description: 'Platform designed specifically for SMBs with growth scalability and intuitive self-service capabilities.',
        metrics: 'SMB optimized',
        color: 'from-green-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Predictive Analytics',
        description: 'Machine learning algorithms provide predictive insights for turnover, performance, and engagement trends.',
        metrics: 'Predictive AI',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Custom Workflow Automation',
        description: 'Flexible workflow builder for onboarding, performance reviews, and approval processes reducing tickets by 80%.',
        metrics: '80% ticket reduction',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Comprehensive Self-Service Portal',
        description: 'Full-featured employee self-service portal with mobile-first design and offline capabilities.',
        metrics: 'Mobile-first',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Extensive Integration Ecosystem',
        description: 'Seamless integration with 500+ business applications including ERP, CRM, and productivity tools.',
        metrics: '500+ integrations',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Advanced Reporting & Analytics',
        description: 'Customizable reports with drag-and-drop builder, automated distribution, and strategic benchmarking.',
        metrics: 'Custom reporting',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Multi-Entity Management',
        description: 'Manage multiple locations, departments, and entities with centralized control and local autonomy.',
        metrics: 'Multi-entity support',
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'employee-engagement-sentiment-platform': {
    keyBenefits: [
      {
        icon: Heart,
        title: 'Engagement Score Tracking',
        description: 'Real-time engagement monitoring with pulse surveys increasing retention by 45%.',
        metrics: '45% retention boost',
        color: 'from-pink-500 to-red-600'
      },
      {
        icon: Users,
        title: 'Culture Analytics',
        description: 'Deep insights into workplace culture with sentiment analysis and culture fit assessments.',
        metrics: 'Culture insights',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Employee Net Promoter Score',
        description: 'Track eNPS with automated surveys and benchmark against industry standards.',
        metrics: 'eNPS tracking',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: MessageSquare,
        title: 'Feedback & Recognition',
        description: 'Peer-to-peer recognition system with social feeds increasing engagement by 60%.',
        metrics: '60% engagement boost',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Wellbeing Insights',
        description: 'Employee wellbeing analytics with stress indicators and mental health support resources.',
        metrics: 'Wellbeing analytics',
        color: 'from-orange-500 to-yellow-600'
      },
      {
        icon: Award,
        title: 'Team Connection Tools',
        description: 'Virtual team building activities and collaboration tools fostering stronger workplace relationships.',
        metrics: 'Team connection',
        color: 'from-teal-500 to-green-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI Sentiment Analysis',
        description: 'Natural language processing analyzes employee communications for sentiment trends.',
        metrics: 'AI sentiment tracking',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Predictive Turnover Alerts',
        description: 'Machine learning predicts employee flight risk with 90% accuracy for proactive intervention.',
        metrics: '90% prediction accuracy',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Manager Coaching Insights',
        description: 'Personalized manager recommendations for improving team engagement and performance.',
        metrics: 'Manager coaching',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Engagement Action Plans',
        description: 'Automated action plan generation based on survey results and engagement data.',
        metrics: 'Automated action plans',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Benchmark Analytics',
        description: 'Industry benchmarking with peer comparison and best practice recommendations.',
        metrics: 'Industry benchmarks',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Custom Survey Builder',
        description: 'Drag-and-drop survey creation with advanced question types and conditional logic.',
        metrics: 'Custom surveys',
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'employee-learning-development-platform': {
    keyBenefits: [
      {
        icon: GraduationCap,
        title: 'AI-Powered Personalized Learning',
        description: 'Intelligent learning paths with experiential design increasing knowledge retention by 80% and completion by 70%.',
        metrics: '80% retention boost',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Skills Gap Analysis & Development',
        description: 'Automated skills assessment identifying gaps and recommending targeted development programmes.',
        metrics: 'Skills gap analysis',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Award,
        title: 'Certification & Compliance Management',
        description: 'Complete certification tracking with automated reminders and compliance reporting reducing violations by 85%.',
        metrics: '85% compliance boost',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Interactive Learning Analytics',
        description: 'Comprehensive learning analytics with immersive content delivery and engagement tracking.',
        metrics: 'Immersive analytics',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Users,
        title: 'Social & Collaborative Learning',
        description: 'Peer learning networks with mentorship programmes and collaborative knowledge sharing.',
        metrics: 'Collaborative learning',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Target,
        title: 'Career Progression Planning',
        description: 'AI-driven career pathing aligned with organizational needs and employee aspirations.',
        metrics: 'Career planning',
        color: 'from-green-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'Adaptive Learning AI',
        description: 'Machine learning algorithms adapt content difficulty and pace based on individual learning patterns.',
        metrics: 'Adaptive AI',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Microlearning & Just-in-Time Training',
        description: 'Bite-sized learning modules delivered at point of need with mobile-first design.',
        metrics: 'Microlearning',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Globe,
        title: 'Multi-Format Content Library',
        description: 'Extensive library with videos, simulations, VR experiences, and interactive assessments.',
        metrics: 'Multi-format content',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Gamification & Engagement',
        description: 'Gamified learning experiences with badges, leaderboards, and achievement tracking.',
        metrics: 'Gamification',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Learning Impact Measurement',
        description: 'ROI tracking connecting learning activities to business outcomes and performance improvements.',
        metrics: 'Impact measurement',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Integration with HR Ecosystem',
        description: 'Seamless integration with HRIS, performance management, and talent development systems.',
        metrics: 'Ecosystem integration',
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'skills-mapping-platform': {
    keyBenefits: [
      {
        icon: Target,
        title: 'Comprehensive Skills Inventory',
        description: 'AI-powered skills assessment mapping current workforce capabilities with 95% accuracy.',
        metrics: '95% accuracy',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Skills Gap Analysis',
        description: 'Automated identification of skill gaps with development recommendations reducing time-to-competency by 60%.',
        metrics: '60% faster competency',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Users,
        title: 'Talent Mobility Platform',
        description: 'Internal talent marketplace matching employees to opportunities based on skills and aspirations.',
        metrics: 'Internal mobility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Workforce Planning Intelligence',
        description: 'Predictive workforce analytics for strategic skills planning and resource allocation.',
        metrics: 'Workforce intelligence',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Award,
        title: 'Skills-Based Career Pathing',
        description: 'Dynamic career progression paths based on current skills and development goals.',
        metrics: 'Career pathing',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Lightbulb,
        title: 'Learning Recommendation Engine',
        description: 'Personalized learning suggestions based on skills gaps and career objectives.',
        metrics: 'Learning recommendations',
        color: 'from-green-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI Skills Recognition',
        description: 'Machine learning algorithms automatically identify and catalog skills from work history and projects.',
        metrics: 'AI skills detection',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Skills Tracking',
        description: 'Dynamic skills updating based on completed projects, training, and performance evaluations.',
        metrics: 'Real-time tracking',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Peer Skills Validation',
        description: 'Crowd-sourced skills verification through peer endorsements and project collaboration.',
        metrics: 'Peer validation',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Skills Marketplace',
        description: 'Internal gig platform connecting skill holders with project needs across the organization.',
        metrics: 'Skills marketplace',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Skills Analytics Dashboard',
        description: 'Executive dashboard showing organizational skills health, gaps, and development ROI.',
        metrics: 'Skills analytics',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'External Skills Benchmarking',
        description: 'Industry comparison of skills inventory with competitive analysis and market trends.',
        metrics: 'Market benchmarking',
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'inclusive-hiring-platform': {
    keyBenefits: [
      {
        icon: Users,
        title: 'Bias-Free Recruitment',
        description: 'AI algorithms eliminate unconscious bias increasing diverse candidate selection by 70%.',
        metrics: '70% diversity boost',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: Target,
        title: 'Inclusive Job Descriptions',
        description: 'AI-powered job posting optimization removing biased language and attracting diverse candidates.',
        metrics: 'Inclusive language',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Diversity Analytics Dashboard',
        description: 'Real-time diversity metrics tracking progress against inclusion goals and benchmarks.',
        metrics: 'Diversity tracking',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Award,
        title: 'Structured Interview Framework',
        description: 'Standardized interview processes ensuring fair evaluation and consistent candidate experience.',
        metrics: 'Structured interviews',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: TrendingUp,
        title: 'Inclusive Sourcing Networks',
        description: 'Partnerships with diverse professional organizations expanding candidate reach by 300%.',
        metrics: '300% reach expansion',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Clock,
        title: 'Compliance Monitoring',
        description: 'Automated EEO compliance tracking with audit trails and regulatory reporting.',
        metrics: 'EEO compliance',
        color: 'from-green-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'Unconscious Bias Detection',
        description: 'Machine learning identifies and flags potential bias in hiring decisions and processes.',
        metrics: 'Bias detection',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Anonymous Resume Screening',
        description: 'Blind resume review process focusing on qualifications while removing identifying information.',
        metrics: 'Anonymous screening',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Inclusive Interview Panels',
        description: 'Automated diverse interview panel composition with bias training recommendations.',
        metrics: 'Diverse panels',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Accessibility Accommodations',
        description: 'Comprehensive accommodation management for candidates with disabilities.',
        metrics: 'Accessibility support',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Pay Equity Analytics',
        description: 'Proactive pay gap analysis with recommendations for equitable compensation decisions.',
        metrics: 'Pay equity',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Inclusion Measurement Tools',
        description: 'Comprehensive inclusion metrics beyond demographics including belonging and engagement.',
        metrics: 'Inclusion metrics',
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  // Remaining 12 HR Products to Complete 23 Total
  'employee-performance-analytics': {
    keyBenefits: [
      {
        icon: BarChart3,
        title: 'Performance Intelligence Dashboard',
        description: 'Real-time performance analytics with predictive insights improving team productivity by 45%.',
        metrics: '45% productivity boost',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Goal Achievement Tracking',
        description: 'Automated goal progress monitoring with milestone alerts and completion predictions.',
        metrics: 'Goal tracking',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Users,
        title: 'Team Performance Insights',
        description: 'Comprehensive team analytics identifying high performers and improvement opportunities.',
        metrics: 'Team insights',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Performance Calibration Tools',
        description: 'AI-assisted calibration ensuring consistent performance ratings across the organization.',
        metrics: 'Calibration consistency',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Award,
        title: 'Recognition & Rewards Analytics',
        description: 'Data-driven recognition programs measuring impact on engagement and retention.',
        metrics: 'Recognition impact',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Clock,
        title: '360-Degree Feedback Analysis',
        description: 'Comprehensive feedback analytics with sentiment analysis and development recommendations.',
        metrics: '360° analysis',
        color: 'from-green-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'Predictive Performance Modeling',
        description: 'Machine learning predicts future performance trends and identifies at-risk employees.',
        metrics: 'Performance prediction',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Performance Alerts',
        description: 'Instant notifications for performance anomalies and milestone achievements.',
        metrics: 'Real-time alerts',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Peer Comparison Analytics',
        description: 'Anonymous peer benchmarking with role-based performance comparisons.',
        metrics: 'Peer benchmarking',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Custom Performance Metrics',
        description: 'Flexible metric builder for role-specific and organizational performance indicators.',
        metrics: 'Custom metrics',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Performance Trend Analysis',
        description: 'Long-term trend identification with seasonal adjustments and cyclical patterns.',
        metrics: 'Trend analysis',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Executive Performance Dashboard',
        description: 'C-suite dashboard with organizational performance health and strategic insights.',
        metrics: 'Executive insights',          
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'workforce-planning-tool': {
    keyBenefits: [
      {
        icon: Users,
        title: 'Strategic Workforce Modeling',
        description: 'Predictive workforce planning models aligning talent strategy with business objectives.',
        metrics: 'Strategic alignment',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Demand Forecasting',
        description: 'AI-powered demand prediction for skills, roles, and headcount reducing hiring lag by 60%.',
        metrics: '60% faster hiring',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Scenario Planning Tools',
        description: 'Multiple workforce scenarios for strategic decision-making and risk mitigation planning.',
        metrics: 'Scenario modeling',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Skills Gap Analysis',
        description: 'Comprehensive skills inventory analysis identifying current and future capability gaps.',
        metrics: 'Skills analysis',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Award,
        title: 'Succession Planning',
        description: 'Automated succession planning with talent pipeline development and readiness assessment.',
        metrics: 'Succession planning',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Clock,
        title: 'Real-Time Planning Dashboard',
        description: 'Executive dashboard providing real-time workforce insights and planning recommendations.',
        metrics: 'Real-time insights',
        color: 'from-green-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'Predictive Analytics Engine',
        description: 'Machine learning models predicting workforce trends, turnover, and skill evolution.',
        metrics: 'Predictive modeling',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Automated Workforce Alerts',
        description: 'Proactive alerts for workforce risks, opportunities, and planning milestones.',
        metrics: 'Automated alerts',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Internal Mobility Optimization',
        description: 'AI-powered internal talent movement recommendations maximizing career growth.',
        metrics: 'Mobility optimization',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Cost-Benefit Analysis',
        description: 'Comprehensive workforce investment analysis with ROI calculations and budget planning.',
        metrics: 'Cost-benefit analysis',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Market Intelligence Integration',
        description: 'External market data integration for competitive workforce intelligence and benchmarking.',
        metrics: 'Market intelligence',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Multi-Dimensional Planning',
        description: 'Complex workforce modeling across geography, business units, and time horizons.',
        metrics: 'Multi-dimensional',
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'benefits-optimization': {
    keyBenefits: [
      {
        icon: DollarSign,
        title: 'Cost Optimization Engine',
        description: 'AI-powered benefits cost analysis reducing total benefits spend by 25% while maintaining satisfaction.',
        metrics: '25% cost reduction',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Users,
        title: 'Personalized Benefits Recommendations',
        description: 'Individual benefits optimization based on demographics, life events, and usage patterns.',
        metrics: 'Personalized benefits',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Benefits Utilization Analytics',
        description: 'Comprehensive analysis of benefits usage identifying underutilized and high-value programs.',
        metrics: 'Utilization insights',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'ROI Measurement Tools',
        description: 'Real-time ROI tracking for all benefits programs with satisfaction correlation analysis.',
        metrics: 'ROI tracking',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Benefits Modeling',
        description: 'Forecast future benefits costs and utilization trends for strategic planning.',
        metrics: 'Predictive modeling',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Award,
        title: 'Competitive Benchmarking',
        description: 'Industry benchmarking ensuring competitive benefits packages while optimizing costs.',
        metrics: 'Market benchmarking',
        color: 'from-teal-500 to-blue-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI Benefits Advisor',
        description: 'Intelligent virtual advisor helping employees select optimal benefits combinations.',
        metrics: 'AI advisor',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Dynamic Benefits Adjustments',
        description: 'Real-time benefits recommendations based on life events and changing needs.',
        metrics: 'Dynamic adjustments',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Benefits Communication Optimizer',
        description: 'Personalized benefits communication strategies increasing engagement by 70%.',
        metrics: '70% engagement boost',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Vendor Performance Analytics',
        description: 'Comprehensive vendor evaluation with performance scoring and cost-effectiveness analysis.',
        metrics: 'Vendor analytics',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Benefits Portfolio Optimization',
        description: 'Mathematical optimization of entire benefits portfolio for maximum value and satisfaction.',
        metrics: 'Portfolio optimization',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Predictive Health Analytics',
        description: 'Health trend analysis for proactive wellness program design and cost management.',
        metrics: 'Health analytics',
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'remote-work-enablement': {
    keyBenefits: [
      {
        icon: Wifi,
        title: 'Remote Collaboration Hub',
        description: 'Comprehensive remote work platform increasing distributed team productivity by 65%.',
        metrics: '65% productivity boost',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: Users,
        title: 'Virtual Team Management',
        description: 'Advanced tools for managing remote teams with performance tracking and engagement monitoring.',
        metrics: 'Virtual management',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Clock,
        title: 'Flexible Work Scheduling',
        description: 'AI-powered scheduling optimization for global teams across time zones and work preferences.',
        metrics: 'Flexible scheduling',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Remote Performance Analytics',
        description: 'Specialized performance metrics for remote work measuring output, collaboration, and engagement.',
        metrics: 'Remote analytics',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Building,
        title: 'Digital Workplace Experience',
        description: 'Virtual office environment replicating in-person collaboration and culture online.',
        metrics: 'Digital workplace',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Heart,
        title: 'Remote Employee Wellbeing',
        description: 'Wellbeing monitoring and support systems preventing remote work isolation and burnout.',
        metrics: 'Wellbeing support',
        color: 'from-green-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI Meeting Optimization',
        description: 'Intelligent meeting scheduling and facilitation reducing meeting fatigue by 50%.',
        metrics: '50% fatigue reduction',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Automated Workflow Orchestration',
        description: 'Seamless workflow automation across remote tools and platforms with smart integrations.',
        metrics: 'Workflow automation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Virtual Culture Building',
        description: 'Digital culture initiatives and team building activities fostering remote team cohesion.',
        metrics: 'Culture building',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Remote Learning & Development',
        description: 'Specialized remote learning programs for skill development and career advancement.',
        metrics: 'Remote learning',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Productivity Intelligence',
        description: 'Advanced analytics identifying optimal work patterns and productivity enhancement opportunities.',
        metrics: 'Productivity intelligence',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Remote Work ROI Analysis',
        description: 'Comprehensive ROI measurement of remote work programs with cost-benefit analysis.',
        metrics: 'ROI analysis',
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'hr-service-delivery': {
    keyBenefits: [
      {
        icon: Headphones,
        title: 'AI-Powered HR Helpdesk',
        description: 'Intelligent virtual assistant resolving 80% of HR queries instantly with 24/7 availability.',
        metrics: '80% instant resolution',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: Users,
        title: 'Employee Self-Service Portal',
        description: 'Comprehensive self-service capabilities reducing HR ticket volume by 75%.',
        metrics: '75% ticket reduction',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Clock,
        title: 'Service Level Management',
        description: 'Automated SLA tracking and management ensuring consistent service delivery quality.',
        metrics: 'SLA management',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Case Management System',
        description: 'Advanced case tracking with automated escalation and resolution workflow management.',
        metrics: 'Case management',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Service Analytics Dashboard',
        description: 'Real-time service performance metrics with satisfaction tracking and improvement insights.',
        metrics: 'Service analytics',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Award,
        title: 'Knowledge Management Hub',
        description: 'Centralized knowledge base with AI-powered content curation and search capabilities.',
        metrics: 'Knowledge hub',
        color: 'from-green-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'Predictive Service Analytics',
        description: 'Machine learning predicts service demand and identifies potential issues before they escalate.',
        metrics: 'Predictive analytics',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Automated Service Routing',
        description: 'Intelligent ticket routing to appropriate specialists based on content analysis and expertise.',
        metrics: 'Smart routing',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Multi-Channel Support Integration',
        description: 'Unified support across email, chat, phone, and mobile with seamless experience continuity.',
        metrics: 'Multi-channel support',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Performance Optimization Engine',
        description: 'Continuous service performance optimization with automated process improvement suggestions.',
        metrics: 'Performance optimization',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Service Satisfaction Analytics',
        description: 'Advanced sentiment analysis and satisfaction tracking with actionable improvement recommendations.',
        metrics: 'Satisfaction analytics',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Workforce Analytics Integration',
        description: 'Service delivery insights integrated with workforce analytics for holistic HR optimization.',
        metrics: 'Integrated analytics',
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'diversity-inclusion-analytics': {
    keyBenefits: [
      {
        icon: Users,
        title: 'Comprehensive D&I Metrics',
        description: 'Advanced diversity analytics tracking representation, progression, and inclusion across all dimensions.',
        metrics: 'D&I metrics',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Pay Gap Analysis',
        description: 'Automated pay equity analysis identifying gaps and providing recommendations for fair compensation.',
        metrics: 'Pay equity analysis',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Inclusion Measurement',
        description: 'Advanced inclusion surveys and sentiment analysis measuring belonging and psychological safety.',
        metrics: 'Inclusion measurement',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Progression Analytics',
        description: 'Career advancement tracking identifying barriers and opportunities for underrepresented groups.',
        metrics: 'Progression tracking',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Award,
        title: 'D&I Benchmarking',
        description: 'Industry benchmarking with peer comparison and best practice recommendations.',
        metrics: 'Industry benchmarking',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: MessageSquare,
        title: 'Cultural Assessment Tools',
        description: 'Comprehensive culture evaluation with bias detection and inclusive behavior measurement.',
        metrics: 'Culture assessment',
        color: 'from-green-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI Bias Detection',
        description: 'Machine learning algorithms identify unconscious bias in hiring, promotion, and performance decisions.',
        metrics: 'Bias detection',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Real-Time D&I Alerts',
        description: 'Proactive alerts for diversity trends, potential bias incidents, and inclusion risks.',
        metrics: 'Real-time alerts',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Intersectional Analysis',
        description: 'Advanced intersectional diversity analysis examining multiple identity dimensions simultaneously.',
        metrics: 'Intersectional analysis',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'D&I Goal Tracking',
        description: 'Automated tracking of diversity goals with progress monitoring and achievement predictions.',
        metrics: 'Goal tracking',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive D&I Modeling',
        description: 'Predictive analytics forecasting diversity trends and intervention impact assessment.',
        metrics: 'Predictive modeling',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Custom D&I Dashboards',
        description: 'Executive and manager dashboards with role-specific D&I insights and action recommendations.',
        metrics: 'Custom dashboards',
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'wellness-mental-health': {
    keyBenefits: [
      {
        icon: Heart,
        title: 'Holistic Wellbeing Platform',
        description: 'Comprehensive wellness program increasing employee wellbeing scores by 60% with integrated support.',
        metrics: '60% wellbeing boost',
        color: 'from-pink-500 to-red-600'
      },
      {
        icon: Activity,
        title: 'Mental Health Monitoring',
        description: 'Proactive mental health assessment with early intervention and support resource recommendations.',
        metrics: 'Mental health monitoring',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: Users,
        title: 'Peer Support Networks',
        description: 'Employee resource groups and peer support networks fostering community and mental wellness.',
        metrics: 'Peer support',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Stress Management Tools',
        description: 'AI-powered stress detection with personalized coping strategies and mindfulness interventions.',
        metrics: 'Stress management',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: TrendingUp,
        title: 'Wellness Analytics Dashboard',
        description: 'Real-time wellness metrics tracking engagement, outcomes, and program effectiveness.',
        metrics: 'Wellness analytics',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Award,
        title: 'Work-Life Balance Optimization',
        description: 'Intelligent workload management and time-off recommendations preventing burnout.',
        metrics: 'Work-life balance',
        color: 'from-yellow-500 to-orange-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI Wellness Coaching',
        description: 'Personalized AI wellness coach providing 24/7 support and evidence-based interventions.',
        metrics: 'AI coaching',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Crisis Detection & Response',
        description: 'Automated crisis detection with immediate intervention and professional support connection.',
        metrics: 'Crisis detection',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Manager Wellbeing Tools',
        description: 'Manager training and tools for supporting team mental health and wellbeing effectively.',
        metrics: 'Manager tools',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Personalized Wellness Plans',
        description: 'Individual wellness plans based on health assessments, goals, and lifestyle preferences.',
        metrics: 'Personalized plans',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Wellbeing Analytics',
        description: 'Machine learning predicts wellbeing risks and identifies optimal intervention timing.',
        metrics: 'Predictive wellbeing',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'ROI & Impact Measurement',
        description: 'Comprehensive wellness program ROI measurement with productivity and engagement correlation.',
        metrics: 'ROI measurement',
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'payroll-benefits-platform': {
    keyBenefits: [
      {
        icon: DollarSign,
        title: 'Automated Payroll Processing',
        description: 'End-to-end payroll automation reducing processing time by 85% with guaranteed accuracy.',
        metrics: '85% time reduction',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: FileText,
        title: 'Integrated Benefits Administration',
        description: 'Seamless benefits management with enrollment, eligibility tracking, and carrier integrations.',
        metrics: 'Benefits integration',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: Users,
        title: 'Employee Self-Service Portal',
        description: 'Comprehensive self-service capabilities for pay stubs, tax documents, and benefits changes.',
        metrics: 'Self-service portal',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Compliance Management',
        description: 'Automated tax calculations, regulatory compliance, and audit trail management.',
        metrics: 'Compliance automation',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Real-Time Reporting',
        description: 'Executive dashboards with real-time payroll and benefits analytics and cost tracking.',
        metrics: 'Real-time reporting',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Clock,
        title: 'Multi-Location Support',
        description: 'Global payroll processing with local compliance and multi-currency support.',
        metrics: 'Global support',
        color: 'from-teal-500 to-blue-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'Predictive Payroll Analytics',
        description: 'AI-powered cost forecasting and budget planning with scenario modeling capabilities.',
        metrics: 'Predictive analytics',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Earnings Processing',
        description: 'Instant pay calculations and on-demand pay access improving employee financial wellness.',
        metrics: 'Real-time processing',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Advanced Benefits Optimization',
        description: 'AI-driven benefits recommendations and cost optimization strategies for employers.',
        metrics: 'Benefits optimization',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Tax Optimization Engine',
        description: 'Intelligent tax planning and optimization reducing overall tax burden legally and efficiently.',
        metrics: 'Tax optimization',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Compensation Analytics',
        description: 'Advanced compensation analysis with market benchmarking and pay equity assessments.',
        metrics: 'Compensation analytics',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Custom Integration Hub',
        description: 'Flexible API platform connecting with HRIS, time tracking, and financial systems.',
        metrics: 'Integration hub',
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'organizational-development': {
    keyBenefits: [
      {
        icon: Building,
        title: 'Organizational Design & Restructuring',
        description: 'AI-powered organizational design optimization improving efficiency and reducing hierarchical bottlenecks.',
        metrics: 'Design optimization',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: Users,
        title: 'Change Management Platform',
        description: 'Comprehensive change management with stakeholder engagement and adoption tracking.',
        metrics: 'Change management',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Culture Transformation Tools',
        description: 'Data-driven culture change initiatives with measurement and intervention recommendations.',
        metrics: 'Culture transformation',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Leadership Development Programs',
        description: 'Personalized leadership development with 360-feedback, coaching, and skill building.',
        metrics: 'Leadership development',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Organizational Health Metrics',
        description: 'Comprehensive organizational health assessment with benchmarking and improvement planning.',
        metrics: 'Health metrics',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Award,
        title: 'Team Effectiveness Analysis',
        description: 'Team performance optimization with collaboration patterns and effectiveness measurement.',
        metrics: 'Team effectiveness',
        color: 'from-green-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI Organizational Intelligence',
        description: 'Machine learning analysis of organizational patterns identifying optimization opportunities.',
        metrics: 'AI intelligence',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Change Impact Analysis',
        description: 'Continuous monitoring of change initiatives with real-time impact assessment and adjustment.',
        metrics: 'Impact analysis',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Network Analysis & Mapping',
        description: 'Organizational network analysis revealing influence patterns and communication flows.',
        metrics: 'Network analysis',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Predictive Organizational Modeling',
        description: 'Scenario planning and predictive modeling for organizational structure and culture changes.',
        metrics: 'Predictive modeling',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Continuous Improvement Engine',
        description: 'Automated identification and implementation of organizational improvement opportunities.',
        metrics: 'Continuous improvement',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Executive Coaching Analytics',
        description: 'Data-driven executive coaching with progress tracking and leadership effectiveness measurement.',
        metrics: 'Coaching analytics',
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  // Missing HR Products - Adding Complete Enhanced Features
  'employee-journey-mapping': {
    keyBenefits: [
      {
        icon: Activity,
        title: 'Complete Lifecycle Visibility',
        description: 'End-to-end employee journey mapping improving experience satisfaction by 70% across all touchpoints.',
        metrics: '70% experience boost',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Touchpoint Optimization',
        description: 'AI-powered touchpoint analysis identifying pain points and optimization opportunities throughout the journey.',
        metrics: 'Touchpoint optimization',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Users,
        title: 'Persona-Based Journey Design',
        description: 'Customized journey experiences based on employee personas, roles, and individual preferences.',
        metrics: 'Persona customisation',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Critical Moment Identification',
        description: 'Advanced analytics identifying key moments that matter most in the employee experience.',
        metrics: 'Critical moments',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Experience Measurement',
        description: 'Real-time experience tracking with sentiment analysis and feedback collection at every stage.',
        metrics: 'Experience tracking',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Award,
        title: 'Cross-Department Collaboration',
        description: 'Unified platform enabling seamless collaboration between HR, IT, and business units.',
        metrics: 'Cross-dept collaboration',
        color: 'from-green-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'Predictive Journey Analytics',
        description: 'Machine learning predicts journey bottlenecks and employee satisfaction risks before they occur.',
        metrics: 'Predictive analytics',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Journey Alerts',
        description: 'Automated alerts when employees encounter friction points or negative experience indicators.',
        metrics: 'Real-time alerts',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Journey Automation Engine',
        description: 'Automated journey orchestration with smart routing and personalized experience delivery.',
        metrics: 'Journey automation',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Benchmark Comparison',
        description: 'Industry benchmarking comparing your employee journey metrics against best-in-class organizations.',
        metrics: 'Industry benchmarking',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Dynamic Journey Optimization',
        description: 'Continuous journey improvement using A/B testing and experimentation frameworks.',
        metrics: 'Dynamic optimization',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Executive Journey Dashboards',
        description: 'Executive-level dashboards with journey KPIs, ROI tracking, and strategic recommendations.',
        metrics: 'Executive dashboards',
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'compensation-analysis-tool': {
    keyBenefits: [
      {
        icon: DollarSign,
        title: 'Market Salary Benchmarking',
        description: 'Comprehensive salary benchmarking against industry standards reducing pay inequity by 85%.',
        metrics: '85% pay equity boost',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: TrendingUp,
        title: 'Pay Equity Analysis',
        description: 'Advanced statistical analysis identifying pay gaps and providing actionable remediation strategies.',
        metrics: 'Pay equity analysis',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: Target,
        title: 'Compensation Band Management',
        description: 'Strategic compensation band optimization ensuring competitive positioning and internal equity.',
        metrics: 'Band optimization',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Merit Increase Planning',
        description: 'Data-driven merit increase recommendations with budget impact analysis and ROI projections.',
        metrics: 'Merit planning',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Award,
        title: 'Total Rewards Modeling',
        description: 'Comprehensive total rewards analysis including benefits, bonuses, and non-monetary value.',
        metrics: 'Total rewards',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Users,
        title: 'Executive Compensation Planning',
        description: 'Specialized executive compensation analysis with governance and regulatory compliance.',
        metrics: 'Executive planning',
        color: 'from-indigo-500 to-blue-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Compensation Intelligence',
        description: 'Machine learning algorithms providing predictive compensation recommendations and market trend analysis.',
        metrics: 'AI compensation',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Market Data',
        description: 'Live market data integration providing up-to-the-minute salary and compensation benchmarking.',
        metrics: 'Real-time data',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Custom Survey Builder',
        description: 'Advanced survey creation tools for collecting internal and external compensation data.',
        metrics: 'Custom surveys',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'ROI & Budget Impact Analysis',
        description: 'Comprehensive financial impact modeling for compensation decisions and budget planning.',
        metrics: 'ROI analysis',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Compensation Modeling',
        description: 'Advanced modeling predicting future compensation needs and market movement trends.',
        metrics: 'Predictive modeling',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Advanced Analytics Suite',
        description: 'Sophisticated analytics with regression analysis, correlation studies, and statistical modeling.',
        metrics: 'Advanced analytics',
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'hr-compliance-dashboard': {
    keyBenefits: [
      {
        icon: FileText,
        title: 'Automated Compliance Monitoring',
        description: 'Continuous monitoring of HR compliance across federal, state, and local regulations.',
        metrics: 'Compliance automation',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: Shield,
        title: 'Risk Assessment & Mitigation',
        description: 'Proactive risk identification with automated mitigation strategies and action plans.',
        metrics: 'Risk mitigation',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Clock,
        title: 'Audit Trail Management',
        description: 'Comprehensive audit trail generation with documentation and evidence management.',
        metrics: 'Audit trails',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Policy Management System',
        description: 'Centralized policy creation, distribution, and acknowledgment tracking with version control.',
        metrics: 'Policy management',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Compliance Reporting Dashboard',
        description: 'Real-time compliance status reporting with executive dashboards and regulatory updates.',
        metrics: 'Compliance reporting',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Users,
        title: 'Training & Certification Tracking',
        description: 'Automated compliance training management with certification tracking and renewal alerts.',
        metrics: 'Training tracking',
        color: 'from-green-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI Compliance Insights',
        description: 'Machine learning analysis of compliance patterns identifying potential violations proactively.',
        metrics: 'AI compliance',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Regulatory Updates',
        description: 'Automated regulatory change monitoring with impact assessment and implementation guidance.',
        metrics: 'Regulatory updates',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Multi-Jurisdiction Management',
        description: 'Complex compliance management across multiple jurisdictions with localized requirements.',
        metrics: 'Multi-jurisdiction',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Incident Management System',
        description: 'Comprehensive incident tracking with investigation workflows and resolution documentation.',
        metrics: 'Incident management',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Compliance Analytics',
        description: 'Predictive modeling identifying future compliance risks and preventive measures.',
        metrics: 'Predictive compliance',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Custom Compliance Frameworks',
        description: 'Flexible framework builder for industry-specific and organizational compliance requirements.',
        metrics: 'Custom frameworks',
        color: 'from-orange-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  }
};